import React, { useState, useEffect, useRef } from 'react';
import { fetchPersonalInfo } from './apiConnector';
import { API_BASE_URL } from '../Api/apiconnector';
import {
  X, ChevronDown, ChevronUp, Camera, CheckCircle,
  User, FileText, CreditCard, ScanFace
} from 'lucide-react';

/* ─── Shared CSS variables (must match DoctorDashboard) ─────────────────── */
const SHARED_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@500;600;700&family=DM+Sans:wght@300;400;500&display=swap');

  :root {
    --r-bg:           #f4f5f7;
    --r-white:        #ffffff;
    --r-border:       #e5e7eb;
    --r-border-light: #f3f4f6;
    --r-accent:       #dc2626;
    --r-accent-hover: #b91c1c;
    --r-accent-light: #fef2f2;
    --r-accent-mid:   #fca5a5;
    --r-text:         #111827;
    --r-sub:          #6b7280;
    --r-muted:        #9ca3af;
    --r-shadow-sm:    0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04);
    --r-shadow-md:    0 4px 12px rgba(0,0,0,0.08);
    --r-font-body:    'DM Sans', sans-serif;
    --r-font-head:    'Syne', sans-serif;
  }
`;

const Sidebar = ({ onClose }) => {
  const [openSection, setOpenSection] = useState(null);
  const [userInfo, setUserInfo]       = useState(null);
  const [showCamera, setShowCamera]   = useState(false);
  const [captureStatus, setCaptureStatus] = useState(null); // 'success'|'error'|null

  const videoRef  = useRef(null);
  const canvasRef = useRef(null);

  const toggleSection = (i) => setOpenSection(openSection === i ? null : i);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetchPersonalInfo();
        setUserInfo(res.data);
      } catch (e) { console.error(e); }
    };
    load();
  }, []);

  useEffect(() => {
    return () => {
      videoRef.current?.srcObject?.getTracks().forEach(t => t.stop());
    };
  }, []);

  /* ── Camera helpers ── */
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch (e) { console.error('Camera error:', e); }
  };

  const stopCamera = () => {
    videoRef.current?.srcObject?.getTracks().forEach(t => t.stop());
    if (videoRef.current) videoRef.current.srcObject = null;
    setShowCamera(false);
    setCaptureStatus(null);
  };

  const captureFace = async () => {
    const canvas = canvasRef.current;
    const video  = videoRef.current;
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const image  = canvas.toDataURL('image/jpeg');
    const userId = userInfo?.user?.id;

    try {
      await fetch(`${API_BASE_URL}/register-face/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, image }),
      });
      setCaptureStatus('success');
      setTimeout(stopCamera, 1500);
    } catch (e) {
      console.error(e);
      setCaptureStatus('error');
    }
  };

  /* ── Sections data ── */
  const sections = userInfo
    ? [
        { icon: <User size={12} />,       title: 'About Me',        content: userInfo.about_me },
        { icon: <FileText size={12} />,   title: 'Case Summary',    content: `Cases Completed: ${userInfo.total_reported}\nPending Reports: ${userInfo.pending_reports || 0}` },
        { icon: <CreditCard size={12} />, title: 'Payment Summary', content: 'Total Earnings: ₹1,25,000\nLast Payment: ₹25,000\nNext Payment Due: 5 Aug 2025' },
      ]
    : [];

  /* ── Render ── */
  return (
    <>
      <style>{SHARED_STYLES}{sidebarStyles}</style>

      {/* Backdrop */}
      <div className="sb-backdrop" onClick={() => { stopCamera(); onClose(); }} />

      {/* Panel */}
      <div className="sb-panel">
        {/* Top accent line */}
        <div className="sb-accent-bar" />

        {/* Header */}
        <div className="sb-header">
          <span className="sb-header-title">
            <ScanFace size={14} className="sb-header-icon" />
            My Profile
          </span>
          <button className="sb-close" onClick={() => { stopCamera(); onClose(); }}>
            <X size={13} strokeWidth={2} />
          </button>
        </div>

        {/* Body */}
        <div className="sb-body">

          {!userInfo ? (
            <div className="sb-loading">
              <div className="sb-spinner" />
              <span>Loading…</span>
            </div>
          ) : (
            <>
              {/* ── Profile card ── */}
              <div className="sb-card sb-profile-card">
                <img
                  src={userInfo.uploadpicture || 'https://thumbs.dreamstime.com/z/portrait-confident-young-doctor-white-background-smiling-31417037.jpg'}
                  alt="Doctor"
                  className="sb-avatar"
                />
                <div className="sb-name">{userInfo.user.first_name} {userInfo.user.last_name}</div>
                <div className="sb-title-text">{userInfo.title}</div>
                <div className="sb-hospital">{userInfo.hospital}</div>

                {/* Stats strip */}
                <div className="sb-stats">
                  <div className="sb-stat">
                    <span className="sb-stat-val">{userInfo.total_reported}</span>
                    <span className="sb-stat-lbl">Reported</span>
                  </div>
                  <div className="sb-stat-div" />
                  <div className="sb-stat">
                    <span className="sb-stat-val">{userInfo.pending_reports || 0}</span>
                    <span className="sb-stat-lbl">Pending</span>
                  </div>
                </div>
              </div>

              {/* ── Register face button ── */}
              <button
                className="sb-btn-face"
                onClick={() => { setShowCamera(true); setCaptureStatus(null); setTimeout(startCamera, 200); }}
              >
                <Camera size={13} strokeWidth={2} />
                Register Face
              </button>

              {/* ── Camera section ── */}
              {showCamera && (
                <div className="sb-card sb-camera-card">
                  {/* Status row */}
                  <div className="sb-cam-status">
                    <span className={`sb-dot${captureStatus === 'success' ? ' dot-success' : captureStatus === 'error' ? ' dot-error' : ' dot-active'}`} />
                    {captureStatus === 'success' ? 'Registered!' :
                     captureStatus === 'error'   ? 'Failed — try again' :
                     'Camera active · look at the camera'}
                  </div>

                  {/* Video */}
                  <div className="sb-video-wrap">
                    <video ref={videoRef} autoPlay playsInline className="sb-video" />
                    <canvas ref={canvasRef} style={{ display: 'none' }} />

                    {/* Corner brackets */}
                    <div className="sb-frame"><div className="sb-frame-inner" /></div>

                    {/* Success overlay */}
                    {captureStatus === 'success' && (
                      <div className="sb-success-overlay">
                        <CheckCircle size={30} strokeWidth={1.5} style={{ color: '#16a34a' }} />
                        <span>Face Registered!</span>
                      </div>
                    )}
                  </div>

                  {captureStatus === 'error' && (
                    <div className="sb-cam-error">Registration failed. Please try again.</div>
                  )}

                  <div className="sb-cam-actions">
                    <button
                      className="sb-btn-capture"
                      onClick={captureFace}
                      disabled={!!captureStatus}
                    >
                      <Camera size={12} strokeWidth={2} /> Capture
                    </button>
                    <button className="sb-btn-cancel" onClick={stopCamera}>Cancel</button>
                  </div>
                </div>
              )}

              {/* ── Collapsible sections ── */}
              {sections.map((sec, i) => (
                <div key={i} className={`sb-section${openSection === i ? ' sb-section-open' : ''}`}>
                  <button className="sb-section-head" onClick={() => toggleSection(i)}>
                    <span className="sb-section-title">
                      <span className="sb-section-icon">{sec.icon}</span>
                      {sec.title}
                    </span>
                    {openSection === i
                      ? <ChevronUp size={13} strokeWidth={2} style={{ color: 'var(--r-muted)' }} />
                      : <ChevronDown size={13} strokeWidth={2} style={{ color: 'var(--r-muted)' }} />}
                  </button>
                  {openSection === i && (
                    <div className="sb-section-body">{sec.content}</div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </>
  );
};

const sidebarStyles = `
  .sb-backdrop {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.25);
    backdrop-filter: blur(2px);
    z-index: 40;
    animation: sbFadeIn .2s ease both;
  }
  @keyframes sbFadeIn { from { opacity:0 } to { opacity:1 } }

  .sb-panel {
    position: fixed; top: 0; right: 0;
    width: 372px; height: 100%;
    background: var(--r-white);
    border-left: 1px solid var(--r-border);
    box-shadow: -4px 0 24px rgba(0,0,0,0.08);
    z-index: 50;
    display: flex; flex-direction: column;
    font-family: var(--r-font-body);
    animation: sbSlideIn .25s cubic-bezier(.22,1,.36,1) both;
  }
  @keyframes sbSlideIn {
    from { transform: translateX(100%); opacity:0 }
    to   { transform: translateX(0);    opacity:1 }
  }

  .sb-accent-bar {
    height: 3px;
    background: var(--r-accent);
    flex-shrink: 0;
  }

  /* Header */
  .sb-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 10px 12px;
    border-bottom: 1px solid var(--r-border-light);
    flex-shrink: 0;
  }
  .sb-header-title {
    display: flex; align-items: center; gap: 6px;
    font-family: var(--r-font-head);
    font-size: 13px; font-weight: 600;
    color: var(--r-text);
  }
  .sb-header-icon { color: var(--r-accent); }
  .sb-close {
    width: 24px; height: 24px;
    display: flex; align-items: center; justify-content: center;
    border-radius: 6px; border: 1px solid var(--r-border);
    background: var(--r-bg); color: var(--r-sub);
    cursor: pointer; transition: all .15s;
  }
  .sb-close:hover { background: var(--r-accent-light); color: var(--r-accent); border-color: var(--r-accent-mid); }

  /* Body scroll */
  .sb-body {
    flex: 1; overflow-y: auto;
    padding: 12px; display: flex; flex-direction: column; gap: 10px;
    scrollbar-width: thin;
    scrollbar-color: var(--r-border) transparent;
  }

  /* Loading */
  .sb-loading {
    flex: 1; display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    gap: 8px; color: var(--r-muted); font-size: 12px;
  }
  .sb-spinner {
    width: 22px; height: 22px;
    border: 2px solid var(--r-border);
    border-top-color: var(--r-accent);
    border-radius: 50%;
    animation: dspin .7s linear infinite;
  }

  /* Cards */
  .sb-card {
    background: var(--r-bg);
    border: 1px solid var(--r-border);
    border-radius: 8px;
    overflow: hidden;
  }

  /* Profile card */
  .sb-profile-card {
    padding: 16px 12px 12px;
    display: flex; flex-direction: column; align-items: center; gap: 3px;
    text-align: center;
  }
  .sb-avatar {
    width: 150px; height: 150px;
    border-radius: 50%;
    object-fit: cover;
    border: 2.5px solid var(--r-accent);
    margin-bottom: 6px;
    box-shadow: var(--r-shadow-sm);
  }
  .sb-name {
    font-family: var(--r-font-head);
    font-size: 13.5px; font-weight: 700;
    color: var(--r-text); line-height: 1.2;
  }
  .sb-title-text { font-size: 11px; color: var(--r-accent); font-weight: 500; }
  .sb-hospital   { font-size: 11px; color: var(--r-muted); }

  /* Stats strip */
  .sb-stats {
    display: flex; align-items: center;
    width: 100%;
    background: var(--r-white);
    border: 1px solid var(--r-border);
    border-radius: 7px;
    overflow: hidden;
    margin-top: 10px;
  }
  .sb-stat {
    flex: 1; padding: 8px 0;
    display: flex; flex-direction: column; align-items: center; gap: 1px;
  }
  .sb-stat-val {
    font-family: var(--r-font-head);
    font-size: 16px; font-weight: 700; color: var(--r-text);
  }
  .sb-stat-lbl {
    font-size: 9.5px; color: var(--r-muted);
    text-transform: uppercase; letter-spacing: .4px;
  }
  .sb-stat-div { width: 1px; height: 32px; background: var(--r-border); }

  /* Face button */
  .sb-btn-face {
    display: flex; align-items: center; justify-content: center; gap: 6px;
    width: 100%; padding: 9px;
    background: var(--r-white);
    border: 1.5px solid var(--r-accent);
    border-radius: 7px;
    color: var(--r-accent);
    font-family: var(--r-font-head);
    font-size: 12px; font-weight: 600;
    cursor: pointer; transition: all .15s;
  }
  .sb-btn-face:hover { background: var(--r-accent); color: white; box-shadow: 0 3px 10px rgba(220,38,38,.2); }

  /* Camera card */
  .sb-camera-card { padding: 10px; display: flex; flex-direction: column; gap: 8px; }

  .sb-cam-status {
    display: flex; align-items: center; gap: 6px;
    font-size: 11px; color: var(--r-sub);
  }
  .sb-dot {
    width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0;
  }
  .dot-active  { background: #3b82f6; animation: dotPulse 1.4s ease-in-out infinite; }
  .dot-success { background: #16a34a; }
  .dot-error   { background: var(--r-accent); }
  @keyframes dotPulse {
    0%,100% { opacity:1; transform:scale(1) }
    50%     { opacity:.4; transform:scale(.75) }
  }

  .sb-video-wrap {
    position: relative; border-radius: 7px;
    overflow: hidden; background: #000;
  }
  .sb-video { width: 100%; display: block; }

  /* Corner frame brackets */
  .sb-frame { position: absolute; inset: 8px; pointer-events: none; }
  .sb-frame::before, .sb-frame::after,
  .sb-frame-inner::before, .sb-frame-inner::after {
    content: ''; position: absolute;
    width: 14px; height: 14px;
    border-color: rgba(220,38,38,.85); border-style: solid;
  }
  .sb-frame::before      { top:0; left:0;  border-width: 2px 0 0 2px; border-radius:2px 0 0 0; }
  .sb-frame::after       { top:0; right:0; border-width: 2px 2px 0 0; border-radius:0 2px 0 0; }
  .sb-frame-inner::before{ bottom:0; left:0;  border-width: 0 0 2px 2px; border-radius:0 0 0 2px; }
  .sb-frame-inner::after { bottom:0; right:0; border-width: 0 2px 2px 0; border-radius:0 0 2px 0; }

  .sb-success-overlay {
    position: absolute; inset:0;
    background: rgba(255,255,255,.82);
    display: flex; flex-direction: column;
    align-items: center; justify-content: center; gap: 4px;
    font-size: 11px; font-weight: 600; color: #15803d;
    animation: sbFadeIn .25s ease both;
  }

  .sb-cam-error {
    font-size: 11px; color: var(--r-accent);
    background: var(--r-accent-light);
    border: 1px solid var(--r-accent-mid);
    border-radius: 6px; padding: 6px 10px; text-align: center;
  }

  .sb-cam-actions { display: flex; gap: 7px; }
  .sb-btn-capture {
    flex: 1; display: flex; align-items: center; justify-content: center; gap: 5px;
    background: #16a34a; color: white; border: none;
    border-radius: 6px; padding: 7px;
    font-family: var(--r-font-head); font-size: 11.5px; font-weight: 600;
    cursor: pointer; transition: background .15s;
  }
  .sb-btn-capture:hover:not(:disabled) { background: #15803d; }
  .sb-btn-capture:disabled { opacity: .5; cursor: not-allowed; }
  .sb-btn-cancel {
    flex: 1; background: var(--r-white);
    border: 1px solid var(--r-border); border-radius: 6px; padding: 7px;
    font-family: var(--r-font-body); font-size: 11.5px; font-weight: 500;
    color: var(--r-sub); cursor: pointer; transition: all .15s;
  }
  .sb-btn-cancel:hover { border-color: var(--r-accent); color: var(--r-accent); }

  /* Collapsible sections */
  .sb-section {
    background: var(--r-white);
    border: 1px solid var(--r-border);
    border-radius: 8px; overflow: hidden;
    transition: border-color .15s;
  }
  .sb-section.sb-section-open { border-color: var(--r-accent-mid); }

  .sb-section-head {
    width: 100%; display: flex; align-items: center; justify-content: space-between;
    padding: 9px 12px; background: none; border: none;
    cursor: pointer; transition: background .12s;
  }
  .sb-section-head:hover { background: var(--r-bg); }

  .sb-section-title {
    display: flex; align-items: center; gap: 6px;
    font-family: var(--r-font-head);
    font-size: 12px; font-weight: 600; color: var(--r-text);
  }
  .sb-section-icon { color: var(--r-accent); display: flex; }

  .sb-section-body {
    padding: 4px 12px 12px 30px;
    font-size: 12px; color: var(--r-sub);
    white-space: pre-wrap; line-height: 1.7;
    border-top: 1px solid var(--r-border-light);
    animation: sbFadeIn .2s ease both;
  }
`;

export default Sidebar;